// Workspace avatar for the far-left column.
//
// Sprint 11b.1 — initial Slack-style 60 px column with deterministic
// auto-color (FNV-1a → HSL hue) and one-letter initial.
// Sprint 11b.2 — supports user-customized 2-character initials and
// 12-color palette via `avatarColor` / `avatarInitials`. Resolution
// happens in `lib/avatar-defaults.resolveAvatar()` so the same logic
// powers the live preview in `EditProjectModal`.
// Sprint project-identity-visibility (D1) — the native `title` tooltip
// carried a browser-fixed ~1 s delay; replaced with a tooltip at delay 0
// (Slack-baseline: rail names show the moment the pointer lands).
// DS.5 (sprint aos-ds-shell, spec §2 D3/D5) — a PRODUCT BLOCK (DS.4 D16:
// user-chosen palette + 2-character initials the DS `Avatar` cannot
// express); the tooltip is the DS piece through ds/DsTooltip.tsx (portal
// contained in `.aos-ds-root`, `delay` forwarded) and the unread count is
// the DS `Badge` (count) — the two arbitrary 18-pixel values it replaced
// were Gate AA's rows for this file.

import { resolveAvatar } from '../../lib/avatar-defaults';
import Badge from '@ds/data-display/Badge';
import { DsTooltip } from '../ds/DsTooltip';

export interface WorkspaceAvatarProps {
  slug: string;
  name: string;
  unreadCount: number;
  isActive: boolean;
  onClick?: () => void;
  avatarColor: string | null;
  avatarInitials: string | null;
  // When true, the wrapper button is replaced with a non-interactive div.
  // Used by the drag preview overlay where the avatar must not steal
  // pointer events from the dnd-kit drag layer.
  presentational?: boolean;
}

// Re-exported so existing call sites (S11b.1 tests) keep working with
// the colour helper. The implementation moved to lib/avatar-defaults.ts.
export { autoColor as avatarColor } from '../../lib/avatar-defaults';

export function WorkspaceAvatar({
  slug,
  name,
  unreadCount,
  isActive,
  onClick,
  avatarColor,
  avatarInitials,
  presentational,
}: WorkspaceAvatarProps) {
  const resolved = resolveAvatar({ name, slug, avatarColor, avatarInitials });

  const inner = (
    <span className="text-body font-550 select-none tracking-tight">
      {resolved.initials}
    </span>
  );

  const baseStyle = {
    width: 40,
    height: 40,
    backgroundColor: resolved.bg,
    color: resolved.fg,
    borderRadius: isActive ? 'var(--radius)' : 'calc(var(--radius) + 4px)',
    outline: isActive ? '2px solid hsl(var(--action))' : 'none',
    outlineOffset: isActive ? '2px' : '0',
  } as const;

  return (
    <div className="relative flex items-center justify-center w-full py-1">
      {isActive && (
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-action"
          data-testid="workspace-active-bar"
        />
      )}
      {presentational ? (
        // Drag preview (D2): no tooltip — the overlay must not steal
        // pointer events from the dnd-kit drag layer (see the
        // `presentational` prop comment above).
        <div
          className="relative flex items-center justify-center transition-all duration-150"
          style={baseStyle}
          data-testid={`workspace-avatar-${slug}`}
          aria-label={name}
        >
          {inner}
        </div>
      ) : (
        <DsTooltip
          content={name}
          position="right"
          delay={0}
          data-testid={`workspace-tooltip-${slug}`}
        >
          <button
            type="button"
            onClick={onClick}
            className="relative flex items-center justify-center transition-all duration-150"
            style={baseStyle}
            data-testid={`workspace-avatar-${slug}`}
            aria-label={name}
            aria-current={isActive ? 'true' : undefined}
          >
            {inner}
          </button>
        </DsTooltip>
      )}

      {unreadCount > 0 && (
        // DS `Badge` count: 18 px, `99+` past maxCount — the same contract the
        // bespoke span carried. Positioned by this outer span (the piece's own
        // wrapper is `position: relative`).
        <span className="absolute -top-0.5 right-1.5" data-testid={`workspace-badge-${slug}`}>
          <Badge variant="count" color="secondary" count={unreadCount} standalone />
        </span>
      )}
    </div>
  );
}
